import React, { useContext, useEffect, useState } from "react";
import { Modal, Form, Input, Select, Alert, Button, Divider } from "antd";
import axios from "axios";
import { BACKEND_URL } from "../../config/config";
import { NotificationContext } from "../context/NotificationWrapper";
import AccountingFirmActivitySelect from "../misc/AccountingFirmActivitySelect";

const { Option } = Select;

/**
 * Modale pour compléter les champs manquants des utilisateurs existants
 * (après refonte du formulaire d'inscription janvier 2026)
 */
const MissingFieldsModal = ({
  visible,
  onClose,
  onSuccess,
  missingFieldsData,
  user,
  activeCompany,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [declarantType, setDeclarantType] = useState('INTERNAL');
  const { openNotification } = useContext(NotificationContext);

  // Déterminer quoi afficher selon les scénarios explicites
  const hasUserMissingFields = missingFieldsData?.userFields
    ? Object.values(missingFieldsData.userFields).some(value => value === true)
    : false;

  const showUserSection = missingFieldsData?.showUserSection === true;

  const hasActiveCompanyMissingFields = activeCompany && missingFieldsData?.companies?.some(
    c => c.membershipCode === activeCompany.membershipCode
  );

  // Déterminer si le bouton Save doit être activé
  // Afficher Save si :
  // 1. Section user affichée (même sans company sélectionnée - permet de sauver user seul), OU
  // 2. Company sélectionnée ET company a des champs manquants
  const showSaveButton = showUserSection || hasActiveCompanyMissingFields;

  // Déterminer quelles sections afficher
  const displayUserSection = showUserSection;
  const displayCompanySection = hasActiveCompanyMissingFields;

  // Initialiser le formulaire avec les données existantes
  useEffect(() => {
    if (visible && user) {
      // Pré-remplir avec les données existantes
      const initialValues = {
        // Champs User
        declarantLastname: user.declarantLastname || '',
        declarantFirstname: user.declarantFirstname || '',
        declarantFunction: user.declarantFunction || '',
        declarantPhone: user.declarantPhone || '',
        declarantType: user.declarantType || 'INTERNAL',
        accountingFirmName: user.accountingFirmName || '',
        accountingFirmActivity: user.accountingFirmActivity || '',
        accountingFirmAddress: user.accountingFirmAddress || '',
        accountingFirmZipcode: user.accountingFirmZipcode || '',
        accountingFirmCity: user.accountingFirmCity || '',
      };

      // Champs Company (seulement si activeCompany est définie)
      if (activeCompany) {
        initialValues.representativeEmail = activeCompany.representativeEmail || '';
        initialValues.representativeLastname = activeCompany.representativeLastname || '';
        initialValues.representativeFirstname = activeCompany.representativeFirstname || '';
        initialValues.representativeFunction = activeCompany.representativeFunction || '';
        initialValues.representativePhone = activeCompany.representativePhone || '';
        initialValues.address1 = activeCompany.address1 || '';
        initialValues.address2 = activeCompany.address2 || '';
        initialValues.address3 = activeCompany.address3 || '';
        initialValues.address4 = activeCompany.address4 || '';
        initialValues.zipcode = activeCompany.zipcode || '';
        initialValues.city = activeCompany.city || '';
        initialValues.referentApasLastname = activeCompany.referentApasLastname || '';
        initialValues.referentApasFirstname = activeCompany.referentApasFirstname || '';
        initialValues.referentApasEmail = activeCompany.referentApasEmail || '';
      }

      setDeclarantType(initialValues.declarantType);
      form.setFieldsValue(initialValues);
    }
  }, [visible, user, activeCompany, form]);

  // Gérer le changement de declarantType
  const handleDeclarantTypeChange = (value) => {
    setDeclarantType(value);
    if (value === 'INTERNAL') {
      // Vider les champs cabinet si INTERNAL
      form.setFieldsValue({
        accountingFirmName: '',
        accountingFirmActivity: '',
        accountingFirmAddress: '',
        accountingFirmZipcode: '',
        accountingFirmCity: '',
      });
    }
  };

  // Gérer la soumission du formulaire
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Déterminer quel endpoint appeler selon ce qui est affiché
      if (displayUserSection && !displayCompanySection) {
        // Mise à jour user uniquement : appeler /api/user/update
        await handleUserOnlySubmit(values);
      } else if (displayCompanySection) {
        // Mise à jour company (avec ou sans user) : appeler /api/company/update
        await handleCompanySubmit(values);
      } else {
        throw new Error('Aucune section à sauvegarder');
      }

      setLoading(false);
      openNotification("Succès", "Vos informations ont été mises à jour", "success");
      onSuccess();
    } catch (error) {
      setLoading(false);
      if (error.response?.data?.data) {
        openNotification("Erreur", error.response.data.data, "error");
      } else if (error.errorFields) {
        openNotification("Erreur", "Veuillez remplir tous les champs obligatoires", "error");
      } else {
        openNotification("Erreur", error.message || "Une erreur est survenue", "error");
      }
      console.error("Erreur lors de la sauvegarde:", error);
    }
  };

  const handleUserOnlySubmit = async (values) => {
    const requestBody = {
      declarantLastname: values.declarantLastname,
      declarantFirstname: values.declarantFirstname,
      declarantFunction: values.declarantFunction,
      declarantPhone: values.declarantPhone,
      declarantType: values.declarantType,
      accountingFirmName: values.declarantType === 'EXTERNAL' ? values.accountingFirmName : null,
      accountingFirmActivity: values.declarantType === 'EXTERNAL' ? values.accountingFirmActivity : null,
      accountingFirmAddress: values.declarantType === 'EXTERNAL' ? values.accountingFirmAddress : null,
      accountingFirmZipcode: values.declarantType === 'EXTERNAL' ? values.accountingFirmZipcode : null,
      accountingFirmCity: values.declarantType === 'EXTERNAL' ? values.accountingFirmCity : null,
    };

    await axios.post(`${BACKEND_URL}/api/user/update`, requestBody, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  };

  const handleCompanySubmit = async (values) => {
    const requestBody = {
      membershipCode: activeCompany.membershipCode,

      // Company - Métier
      laborUnion: activeCompany.laborUnion,
      workForce: activeCompany.workForce,
      buildingConventionOutside77: activeCompany.buildingConventionOutside77,
      agreeOptionalWorker: activeCompany.workForce === "moreThan10" ? activeCompany.agreeOptionalWorker : false,

      // Company - Adresse
      address1: values.address1 || null,
      address2: values.address2,
      address3: values.address3 || null,
      address4: values.address4 || null,
      zipcode: values.zipcode,
      city: values.city,

      // Company - Représentant légal
      representativeLastname: values.representativeLastname,
      representativeFirstname: values.representativeFirstname,
      representativeFunction: values.representativeFunction,
      representativePhone: values.representativePhone,
      representativeEmail: values.representativeEmail || null,

      // Company - Référent APAS
      referentApasLastname: values.referentApasLastname,
      referentApasFirstname: values.referentApasFirstname,
      referentApasEmail: values.referentApasEmail,

      // User - Inclure si section user affichée, sinon utiliser données existantes
      declarantLastname: displayUserSection ? values.declarantLastname : user.declarantLastname,
      declarantFirstname: displayUserSection ? values.declarantFirstname : user.declarantFirstname,
      declarantFunction: displayUserSection ? values.declarantFunction : user.declarantFunction,
      declarantPhone: displayUserSection ? values.declarantPhone : user.declarantPhone,
      declarantType: displayUserSection ? values.declarantType : user.declarantType,

      // User - Cabinet comptable
      accountingFirmName: (displayUserSection && values.declarantType === 'EXTERNAL')
        ? values.accountingFirmName
        : (user.declarantType === 'EXTERNAL' ? user.accountingFirmName : null),
      accountingFirmActivity: (displayUserSection && values.declarantType === 'EXTERNAL')
        ? values.accountingFirmActivity
        : (user.declarantType === 'EXTERNAL' ? user.accountingFirmActivity : null),
      accountingFirmAddress: (displayUserSection && values.declarantType === 'EXTERNAL')
        ? values.accountingFirmAddress
        : (user.declarantType === 'EXTERNAL' ? user.accountingFirmAddress : null),
      accountingFirmZipcode: (displayUserSection && values.declarantType === 'EXTERNAL')
        ? values.accountingFirmZipcode
        : (user.declarantType === 'EXTERNAL' ? user.accountingFirmZipcode : null),
      accountingFirmCity: (displayUserSection && values.declarantType === 'EXTERNAL')
        ? values.accountingFirmCity
        : (user.declarantType === 'EXTERNAL' ? user.accountingFirmCity : null),
    };

    await axios.post(`${BACKEND_URL}/api/company/update`, requestBody, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  };

  // Ne rien afficher si :
  // - Pas visible
  // - Pas de données
  // - Pas de champs manquants globalement
  // - OU (company sélectionnée MAIS aucune section à afficher pour cette company)
  if (!visible || !missingFieldsData || !missingFieldsData.hasMissingFields) {
    return null;
  }

  // Si une company est sélectionnée, mais qu'aucune section n'est à afficher pour elle
  // (user déjà répondu ET cette company déjà répondue), ne pas afficher la modale
  if (activeCompany && !displayUserSection && !displayCompanySection) {
    return null;
  }

  return (
    <Modal
      title="Complétez votre profil"
      open={visible}
      onCancel={onClose}
      width={800}
      maskClosable={false}
      footer={
        !showSaveButton
          ? [
              <Button key="later" onClick={onClose}>
                Plus tard
              </Button>,
            ]
          : [
              <Button key="later" onClick={onClose}>
                Plus tard
              </Button>,
              <Button
                key="submit"
                type="primary"
                loading={loading}
                onClick={handleSubmit}
              >
                Enregistrer
              </Button>,
            ]
      }
    >
      <Alert
        message={
          displayUserSection && !displayCompanySection
            ? "Pour améliorer nos services, veuillez compléter les informations du déclarant. Vous pourrez ensuite compléter les informations de votre entreprise adhérente après l'avoir sélectionnée."
            : displayCompanySection && !displayUserSection
            ? "Pour améliorer nos services et assurer la conformité, veuillez compléter les informations de votre entreprise adhérente."
            : "Pour améliorer nos services et assurer la conformité, veuillez compléter les informations suivantes."
        }
        type="info"
        showIcon
        style={{ marginBottom: 20 }}
      />

      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        {/* Section User : affichée selon le flag backend showUserSection */}
        {displayUserSection && (
          <>
            <Divider orientation="left">Informations du déclarant</Divider>
            <Alert
              message="Ces informations sont partagées entre toutes vos entreprises adhérentes."
              type="info"
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              name="declarantLastname"
              label="Nom"
              rules={[
                { required: true, message: 'Le nom est requis' },
                { max: 255, message: 'Maximum 255 caractères' }
              ]}
            >
              <Input placeholder="Nom du déclarant" />
            </Form.Item>

            <Form.Item
              name="declarantFirstname"
              label="Prénom"
              rules={[
                { required: true, message: 'Le prénom est requis' },
                { max: 255, message: 'Maximum 255 caractères' }
              ]}
            >
              <Input placeholder="Prénom du déclarant" />
            </Form.Item>

            <Form.Item
              name="declarantFunction"
              label="Fonction"
              rules={[
                { required: true, message: 'La fonction est requise' },
                { max: 255, message: 'Maximum 255 caractères' }
              ]}
            >
              <Input placeholder="Fonction du déclarant" />
            </Form.Item>

            <Form.Item
              name="declarantPhone"
              label="Téléphone"
              rules={[
                { required: true, message: 'Le téléphone est requis' },
                { pattern: /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/, message: "Format de téléphone invalide (ex: 0612345678, +33 6 12 34 56 78)" }
              ]}
            >
              <Input placeholder="Téléphone du déclarant" />
            </Form.Item>

            <Form.Item
              name="declarantType"
              label="Type de déclarant"
              rules={[{ required: true, message: 'Le type est requis' }]}
            >
              <Select onChange={handleDeclarantTypeChange}>
                <Option value="INTERNAL">Interne (personne de l'entreprise)</Option>
                <Option value="EXTERNAL">Externe (prestataire externe)</Option>
              </Select>
            </Form.Item>

            {/* Champs cabinet : affichés seulement si declarantType === 'EXTERNAL' */}
            {declarantType === 'EXTERNAL' && (
              <>
                <Divider orientation="left">Prestataire externe</Divider>

                <Form.Item
                  name="accountingFirmName"
                  label="Nom du prestataire (précisez s'il s'agit d'un cabinet comptable)"
                  rules={[
                    { required: true, message: 'Le nom du prestataire est requis' },
                    { max: 255, message: 'Maximum 255 caractères' }
                  ]}
                >
                  <Input placeholder="Nom du prestataire" />
                </Form.Item>

                <Form.Item
                  name="accountingFirmActivity"
                  label="Activité du prestataire externe"
                  rules={[
                    { required: true, message: "L'activité du prestataire est requise" }
                  ]}
                >
                  <AccountingFirmActivitySelect />
                </Form.Item>

                <Form.Item
                  name="accountingFirmAddress"
                  label="Adresse du prestataire"
                  rules={[
                    { required: true, message: 'L\'adresse du prestataire est requise' },
                    { max: 255, message: 'Maximum 255 caractères' }
                  ]}
                >
                  <Input placeholder="Adresse du prestataire" autoComplete="new-password" />
                </Form.Item>

                <Form.Item
                  name="accountingFirmZipcode"
                  label="Code postal du prestataire"
                  rules={[
                    { required: true, message: 'Le code postal du prestataire est requis' },
                    { pattern: /^[0-9]{5}$/, message: 'Code postal à 5 chiffres' }
                  ]}
                >
                  <Input placeholder="Code postal du prestataire" maxLength={5}  autoComplete="new-password"/>
                </Form.Item>

                <Form.Item
                  name="accountingFirmCity"
                  label="Ville du prestataire"
                  rules={[
                    { required: true, message: 'La ville du prestataire est requise' },
                    { max: 255, message: 'Maximum 255 caractères' }
                  ]}
                >
                  <Input placeholder="Ville du prestataire" autoComplete="new-password" />
                </Form.Item>
              </>
            )}
          </>
        )}

        {/* Section Company : affichée selon displayCompanySection */}
        {displayCompanySection && (
          <>
            <Divider orientation="left">Informations de l'adhérent</Divider>

            <Alert
              message={`Entreprise adhérente : ${activeCompany.companyName} (${activeCompany.membershipCode})`}
              type="info"
              style={{ marginBottom: 16 }}
            />

            {/* Champs adresse */}
            <Divider orientation="left">Adresse de l'entreprise</Divider>

            <Form.Item
              name="address1"
              label="Complément d'adresse (optionnel)"
              rules={[{ max: 255, message: 'Maximum 255 caractères' }]}
            >
              <Input placeholder="Bâtiment, étage, appartement..." autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="address2"
              label="Adresse – ligne 1"
              rules={[
                { required: true, message: "L'adresse est requise" },
                { max: 255, message: 'Maximum 255 caractères' }
              ]}
            >
              <Input placeholder="Numéro et nom de rue" autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="address3"
              label="Adresse – ligne 2 (optionnel)"
              rules={[{ max: 255, message: 'Maximum 255 caractères' }]}
            >
              <Input placeholder="Lieu-dit, hameau..." autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="address4"
              label="Votre référence (optionnel)"
              rules={[{ max: 255, message: 'Maximum 255 caractères' }]}
            >
              <Input placeholder="Référence interne" autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="zipcode"
              label="Code postal"
              rules={[
                { required: true, message: 'Le code postal est requis' },
                { pattern: /^[0-9]{5}$/, message: 'Code postal à 5 chiffres' }
              ]}
            >
              <Input placeholder="75000" maxLength={5} autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="city"
              label="Commune"
              rules={[
                { required: true, message: 'La commune est requise' },
                { max: 255, message: 'Maximum 255 caractères' }
              ]}
            >
              <Input placeholder="Paris" autoComplete="new-password" />
            </Form.Item>

            {/* Champs représentant légal */}
            <Divider orientation="left">Représentant légal</Divider>

            <Form.Item
              name="representativeLastname"
              label="Nom du représentant légal"
              rules={[
                { required: true, message: 'Le nom est requis' },
                { pattern: /^[a-zA-ZÀ-ÿ\s'-]+$/, message: "Le nom ne peut contenir que des lettres, espaces, traits d'union et apostrophes" },
                { max: 255, message: 'Maximum 255 caractères' }
              ]}
            >
              <Input placeholder="Nom du représentant" autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="representativeFirstname"
              label="Prénom du représentant légal"
              rules={[
                { required: true, message: 'Le prénom est requis' },
                { pattern: /^[a-zA-ZÀ-ÿ\s'-]+$/, message: "Le prénom ne peut contenir que des lettres, espaces, traits d'union et apostrophes" },
                { max: 255, message: 'Maximum 255 caractères' }
              ]}
            >
              <Input placeholder="Prénom du représentant" autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="representativeFunction"
              label="Fonction du représentant légal"
              rules={[
                { required: true, message: 'La fonction est requise' },
                { pattern: /^[a-zA-ZÀ-ÿ\s'-]+$/, message: "La fonction ne peut contenir que des lettres, espaces, traits d'union et apostrophes" },
                { max: 255, message: 'Maximum 255 caractères' }
              ]}
            >
              <Input placeholder="Gérant, Directeur..." autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="representativePhone"
              label="Téléphone du représentant légal"
              rules={[
                { required: true, message: 'Le téléphone est requis' },
                { pattern: /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/, message: "Format de téléphone invalide (ex: 0612345678, +33 6 12 34 56 78)" }
              ]}
            >
              <Input placeholder="0612345678" autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="representativeEmail"
              label="Email du représentant légal (optionnel)"
              rules={[
                { type: 'email', message: 'Email invalide' }
              ]}
            >
              <Input type="email" placeholder="email@example.com" autoComplete="new-password" />
            </Form.Item>

            {/* Champs référent APAS */}
            <Divider orientation="left">Référent APAS</Divider>
            <Alert
              message="Interlocuteur relais entre l'entreprise, les salariés et l'APAS-BTP.
                Rôle : recevoir les informations de l'association et les diffuser auprès des salariés, orienter les salariés vers l'association si besoin, faire remonter les questions ou besoins identifiés."
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form.Item
              name="referentApasLastname"
              label="Nom du référent APAS"
              rules={[
                { required: true, message: 'Le nom du référent APAS est requis' },
                { max: 255, message: 'Maximum 255 caractères' }
              ]}
            >
              <Input placeholder="Nom" autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="referentApasFirstname"
              label="Prénom du référent APAS"
              rules={[
                { required: true, message: 'Le prénom du référent APAS est requis' },
                { max: 255, message: 'Maximum 255 caractères' }
              ]}
            >
              <Input placeholder="Prénom" autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="referentApasEmail"
              label="Email du référent APAS"
              rules={[
                { required: true, message: "L'email du référent APAS est requis" },
                { type: 'email', message: 'Email invalide' }
              ]}
            >
              <Input type="email" placeholder="email@example.com" autoComplete="new-password" />
            </Form.Item>
              
            
          </>
        )}
      </Form>
    </Modal>
  );
};

export default MissingFieldsModal;
